import { useQuery } from '@tanstack/react-query'
import { getNextFreeGameQuotaReset } from '@repo/sdk'
import { useEffect } from 'react'

import { useAccountDayClock } from '@/hooks/use-account-day-clock'
import { useAccountTimeZone } from '@/hooks/use-account-time-zone'
import { useAuth } from '@/lib/auth'
import { useTRPC } from '@/lib/trpc'

export function useCheckInHistory(range?: { from?: string, to?: string }) {
  const trpc = useTRPC()
  const { actor, authResolved } = useAuth()
  const calendar = useAccountTimeZone()
  const enabled = authResolved && !!actor && calendar.ready
  const query = useQuery({
    ...trpc.userRouter.getCheckInHistory.queryOptions(range),
    enabled,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
  })
  const clock = useAccountDayClock(query.data?.serverTime)
  const { currentTime, refresh } = clock
  const { refetch } = query
  const timeZone = query.data?.timeZone ?? calendar.timeZone
  const nextResetAt = query.data?.nextResetAt

  useEffect(() => {
    if (!enabled)
      return
    let timer: number
    const refreshHistory = () => {
      refresh()
      void refetch()
    }
    const scheduleReset = () => {
      const now = currentTime()
      const serverReset = Date.parse(nextResetAt ?? '')
      const reset = Number.isFinite(serverReset) && serverReset > now
        ? serverReset
        : getNextFreeGameQuotaReset(new Date(now), timeZone).getTime()
      timer = window.setTimeout(() => {
        refreshHistory()
        scheduleReset()
      }, Math.max(0, reset - now + 50))
    }
    scheduleReset()
    window.addEventListener('focus', refreshHistory)
    window.addEventListener('online', refreshHistory)
    return () => {
      window.clearTimeout(timer)
      window.removeEventListener('focus', refreshHistory)
      window.removeEventListener('online', refreshHistory)
    }
  }, [currentTime, enabled, nextResetAt, refetch, refresh, timeZone])

  return {
    ...query,
    calendar,
    nowMs: clock.nowMs,
    isResolving: !authResolved || (!!actor && !calendar.ready && !calendar.isError),
    hasIdentity: !!actor,
  }
}
